import { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  ChevronDown,
  Grid,
  Library,
  Database,
  Users,
  GraduationCap,
  Globe2,
  Leaf,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [shortcutOpen, setShortcutOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  const navbarRef = useRef(null);
  const shortcutRef = useRef(null);

  // Menu data
  const menuItems = [
    { label: "Home", href: "/" },
    {
      label: "About",
      submenu: [
        {
          heading: "About",
          links: [
            { label: "About Us", href: "/about" },
            { label: "Mission and Vision", href: "/mission-vision" },
            { label: "BUBBAN at a Glance", href: "/at-a-glance" },
            { label: "Achievements", href: "/achievements" },
          ],
        },
        {
          heading: "Message",
          links: [
            { label: "Message from Chairman, BoT", href: "/bot-meessage" },
            { label: "Message from Vice Chancellor", href: "/vc" },
            { label: "Message from Pro-Vice Chancellor", href: "/provc" },
            { label: "Message from Treasurer", href: "/treasurer" },
            { label: "Message from Registrar", href: "/registrar-message" },
          ],
        },
        {
          heading: "Activities",
          links: [
            { label: "Notice Board", href: "/all-notice" },
            { label: "News", href: "/all-news" },
            { label: "Events", href: "/all-events" },
            { label: "Forms & Downloads", href: "/forms-downloads" },
            { label: "FAQ", href: "/faq" },
            { label: "Academic Calendar", href: "/academic-calendar" },
          ],
        },
      ],
    },
    {
      label: "Administration",
      submenu: [
        {
          heading: "Administration",
          links: [
            { label: "Hon'ble Vice Chancellor", href: "/vc" },
            { label: "Hon'ble Pro Vice Chancellor", href: "/provc" },
            { label: "Hon'ble Treasurer", href: "/treasurer" },
            { label: "Registrar", href: "/registrar" },
            { label: "Librarian", href: "/librarian" },
            { label: "Controller of Exam", href: "/coe" },
            { label: "Deans of School", href: "/deans" },
            { label: "Proctorial Body", href: "/proctor" },
            { label: "Syndicate", href: "/syndicate" },
            { label: "Academic Council", href: "/academic-council" },
          ],
        },
        {
          heading: "Division",
          links: [
            { label: "Admission and Information Division", href: "/admission-division" },
            { label: "Accounts and Finance Division", href: "/acfd" },
            { label: "Information and Technology Division", href: "/itd" },
            { label: "Public Relations and Publication Office", href: "/pro" },
            { label: "Library and Information Division", href: "/librarian" },
            { label: "Institutional Quality Assurance Cell (IQAC)", href: "/iqac" },
            { label: "Transportation Office", href: "/transport-office" },
            { label: "Audit Cell", href: "/audit-cell" },
            { label: "Security Office", href: "/security-office" },
          ],
        },
      ],
    },
    {
      label: "Admission",
      submenu: [
        {
          heading: "Information",
          links: [
            { label: "Admission Requirements", href: "/admission-requirements" },
            { label: "Admission Advertisement", href: "/admission-advertisements" },
            { label: "Admission Instruction", href: "/admission-instruction" },
            { label: "Admission Test", href: "/admission-test" },
          ],
        },
        {
          heading: "Apply",
          links: [
            { label: "Apply Online", href: "/apply-online" },
            { label: "Download Admission Form", href: "/admission-form" },
            { label: "Download Admit Card", href: "/admit-card" },
            { label: "Admission Result", href: "/admission-result" },
            { label: "Download Offer Letter", href: "/offer-letter" },
          ],
        },
        {
          heading: "Financial",
          links: [
            { label: "Fees", href: "/fees" },
            { label: "Financial Aid", href: "/financial-aid" },
          ],
        },
      ],
    },
    {
      label: "Schools",
      submenu: [
        {
          heading: "Science, Engineering and Technology",
          links: [
            { label: "Computer Science and Engineering", href: "/department/cse" },
          ],
        },
        {
          heading: "Business Administration",
          links: [
            { label: "Business Administration", href: "/department/dba" },
            { label: "Hospitality and Tourism Management", href: "/department/htm" },
          ],
        },
        {
          heading: "Arts, Social Science and Law",
          links: [
            { label: "Governance and Development Studies", href: "/department/gds" },
            { label: "Department of English", href: "/department/eng" },
          ],
        },
      ],
    },
    { label: "Contact", href: "/contact" },
  ];

  // Quick links
  const shortcuts = [
    {
      label: "Library",
      href: "https://opac.bubban.ac.bd",
      icon: Library,
    },
    {
      label: "University Repository",
      href: "https://dspace.bubban.ac.bd",
      icon: Database,
    },
    {
      label: "BUBBAN HR",
      href: "https://hrd.bubban.ac.bd",
      icon: Users,
    },
    {
      label: "Alumni",
      href: "/alumni",
      icon: GraduationCap,
    },
    {
      label: "Office of International Affairs",
      href: "/international-affairs",
      icon: Globe2,
    },
    {
      label: "CERTI",
      href: "/certi",
      icon: Leaf,
    },
  ];

  // Close menus when clicking outside the navbar
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveMenu(null);
        setShortcutOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActiveMenu(null);
        setShortcutOpen(false);
        setOpenSub(null);
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  // Close desktop menus when switching to mobile layout
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setActiveMenu(null);
      } else {
        setMobileOpen(false);
        setOpenSub(null);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle desktop submenu: only one submenu can be open
  const toggleDesktopMenu = (index) => {
    setActiveMenu((current) => (current === index ? null : index));
    setShortcutOpen(false);
  };

  // Toggle mobile accordion submenu
  const toggleMobileSub = (index) => {
    setOpenSub((current) => (current === index ? null : index));
  };

  const closeMenus = () => {
    setActiveMenu(null);
    setOpenSub(null);
    setMobileOpen(false);
    setShortcutOpen(false);
  };

  return (
    <nav
      ref={navbarRef}
      className="sticky top-0 z-50 border-b border-green-200 bg-green-100 shadow-md"
    >
      {/* Main Navbar */}
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4">
        {/* Logo and University Name */}
        <a
          href="/"
          onClick={closeMenus}
          className="flex shrink-0 items-center gap-2"
          aria-label="Bandarban University Home"
        >
          <img
            src="/assets/img/bubbanlogo.jpg"
            alt="Bandarban University Logo"
            className="h-10 w-auto object-contain"
          />

          <span className="text-base font-bold text-green-900 sm:text-lg">
            Bandarban University
          </span>
        </a>

        {/* Desktop Menu */}
        <ul className="hidden items-center gap-5 lg:flex xl:gap-7">
          {menuItems.map((item, i) => (
            <li key={item.label} className="relative">
              {!item.submenu ? (
                <a
                  href={item.href}
                  onClick={closeMenus}
                  className="relative py-2 font-semibold text-gray-700 transition-colors hover:text-green-700"
                >
                  {item.label}
                </a>
              ) : (
                <>
                  {/* Parent Menu Button */}
                  <button
                    type="button"
                    onClick={() => toggleDesktopMenu(i)}
                    aria-expanded={activeMenu === i}
                    aria-haspopup="true"
                    className={`group relative flex items-center gap-1 py-2 font-semibold transition-colors ${
                      activeMenu === i
                        ? "text-green-700"
                        : "text-gray-700 hover:text-green-700"
                    }`}
                  >
                    {item.label}

                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        activeMenu === i ? "rotate-180" : ""
                      }`}
                    />

                    <span
                      className={`absolute -bottom-0.5 left-0 h-0.5 bg-green-600 transition-all duration-300 ${
                        activeMenu === i
                          ? "w-full"
                          : "w-0 group-hover:w-full"
                      }`}
                    />
                  </button>

                  {/* Animated Mega Menu */}
                  <AnimatePresence>
                    {activeMenu === i && (
                      <motion.div
                        key={`desktop-submenu-${i}`}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`
						  fixed top-[86px] left-1/2 -translate-x-1/2
						  w-[calc(100vw-32px)] max-w-[1100px]
						  max-h-[calc(100vh-110px)] overflow-y-auto
						  z-50
						`}
                      >
                        <div
                          className="grid gap-x-8 gap-y-7 rounded-2xl border border-green-100 bg-white/95 p-6 shadow-2xl backdrop-blur-xl sm:p-8"
                          style={{
                            gridTemplateColumns:
                              `repeat(${Math.min(item.submenu.length, 3)}, minmax(0, 1fr))`,
                          }}
                        >
                          {item.submenu.map((col) => (
                            <div key={col.heading} className="group/min-w-0">
                              <h3 className="mb-3 font-bold text-green-800">
                                {col.heading}
                                <span className="mt-2 block h-0.5 w-10 rounded-full bg-green-500 transition-all duration-300 group-hover/min-w-0:w-full" />
                              </h3>

                              <ul className="space-y-2">
                                {col.links.map((link) => (
                                  <li key={link.href + link.label}>
                                    <a
                                      href={link.href}
                                      onClick={closeMenus}
                                      className="flex items-start gap-2 rounded-md py-1 text-sm leading-5 text-gray-700 transition-all duration-200 hover:translate-x-1 hover:text-green-700"
                                    >
                                      <span className="text-green-600">→</span>
                                      <span>{link.label}</span>
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </li>
          ))}
        </ul>

        {/* Right-Side Buttons */}
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3 lg:ml-0">
          {/* Quick Links */}
          <div ref={shortcutRef} className="relative">
            <button
              type="button"
              onClick={() => {
                setShortcutOpen((current) => !current);
                setActiveMenu(null);
              }}
              aria-label="Quick Links"
              aria-expanded={shortcutOpen}
              className="rounded-full bg-white p-2.5 text-green-700 shadow-sm transition-all hover:bg-green-200 hover:shadow-md"
            >
              <Grid size={20} />
            </button>

            <AnimatePresence>
              {shortcutOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 z-50 mt-3 w-[min(440px,90vw)] rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl"
                >
                  {/* Quick Links Header */}
                  <div className="mb-3 flex items-center gap-3 border-b pb-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100">
                      <Grid size={18} className="text-green-700" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-gray-800">
                        Quick Links
                      </h3>
                      <p className="text-xs text-gray-500">
                        Explore university services
                      </p>
                    </div>
                  </div>

                  {/* Quick Links Grid */}
                  <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {shortcuts.map((shortcut) => {
                      const Icon = shortcut.icon;

                      return (
                        <li key={shortcut.label}>
                          <a
                            href={shortcut.href}
                            onClick={closeMenus}
                            className="group flex items-center gap-3 rounded-xl p-3 transition-colors duration-200 hover:bg-green-50"
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-50 transition-colors group-hover:bg-green-700">
                              <Icon
                                size={19}
                                className="text-green-700 transition-colors group-hover:text-white"
                              />
                            </span>

                            <span className="text-sm font-medium leading-5 text-gray-700 transition-colors group-hover:text-green-700">
                              {shortcut.label}
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Login Button */}
          <a
            href="https://web.bubban.ac.bd/login"
            className="rounded-lg bg-orange-500 px-3 py-2 font-semibold text-white shadow-sm transition-all hover:bg-orange-600 sm:px-4"
          >
            Login
          </a>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            className="rounded-lg p-2 text-green-900 transition-colors hover:bg-green-200 lg:hidden"
            onClick={() => {
              setMobileOpen((current) => !current);
              setOpenSub(null);
              setShortcutOpen(false);
            }}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={27} /> : <Menu size={27} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence initial={false}>
        {mobileOpen && (
          <motion.div
            key="mobile-navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-t border-green-200 bg-white lg:hidden"
          >
            <div className="max-h-[75vh] space-y-1 overflow-y-auto p-4">
              {menuItems.map((item, i) => (
                <div
                  key={item.label}
                  className="border-b border-gray-100 last:border-0"
                >
                  {!item.submenu ? (
                    <a
                      href={item.href}
                      onClick={closeMenus}
                      className="block rounded-lg px-3 py-3 font-semibold text-gray-700 transition-colors hover:bg-green-50 hover:text-green-700"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleMobileSub(i)}
                        aria-expanded={openSub === i}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-3 text-left font-semibold transition-colors ${
                          openSub === i
                            ? "bg-green-50 text-green-800"
                            : "text-gray-700 hover:bg-green-50 hover:text-green-700"
                        }`}
                      >
                        {item.label}

                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-300 ${
                            openSub === i ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <AnimatePresence initial={false}>
                        {openSub === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-4 pb-4 pl-5 pr-2 pt-2">
                              {item.submenu.map((col) => (
                                <div key={col.heading}>
                                  <h4 className="mb-2 border-l-2 border-green-500 pl-2 text-sm font-bold text-green-800">
                                    {col.heading}
                                  </h4>

                                  <ul className="space-y-1 pl-2">
                                    {col.links.map((link) => (
                                      <li key={link.href + link.label}>
                                        <a
                                          href={link.href}
                                          onClick={closeMenus}
                                          className="block rounded-md px-2 py-2 text-sm text-gray-600 transition-colors hover:bg-green-50 hover:text-green-700"
                                        >
                                          {link.label}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
